/**
 * The background reconnect (P1.9-T32, the author's ruling of 2026-09-25, answering the brief's question 18): a close a
 * retry can cure (`reconnects`, `disconnect.ts`) leaves the player in the world under a disconnection mark while the
 * page tries again a few times, and only when those attempts are spent does the full-screen disconnect notice
 * (P1.9-T15) show, from which only the player's Reconnect, Reload or Log in tries again.
 *
 * Each attempt waits a jittered delay. After a deploy or a gateway restart every player's page retries at once, so
 * the delays are spread over the window of each attempt rather than fixed, and the whole run is bounded. The knobs
 * are the constants below: `RECONNECT_ATTEMPTS` the number of tries, `RECONNECT_FIRST_MS` the ceiling of the first
 * wait, which doubles each try, `RECONNECT_CAP_MS` the longest wait and `RECONNECT_STREAK_MS` how long a connection
 * must stay up before the next loss starts over at the first wait. With the numbers as they stand a run's attempts
 * together take between about 23 and 46 seconds, and a page opens at most five connections (and, when its sign-in
 * has lapsed, five refreshes, against the account endpoints' 60 a minute per address, ADR-063) before it stops.
 *
 * A connection that is restored and lost again within `RECONNECT_STREAK_MS` is the same streak: the next run's waits
 * continue the doubling from where the last left off, so a gateway that welcomes a page and closes it again each time
 * is not tried every second or two for ever.
 */

/**
 * How many times the page tries to join again in the background before it shows the notice.
 */
export const RECONNECT_ATTEMPTS = 5;

/**
 * The ceiling of the wait before the first attempt, in milliseconds: the jitter below it spreads a crowd that lost
 * its connection together over one to two seconds.
 */
export const RECONNECT_FIRST_MS = 2_000;

/**
 * The longest ceiling of any wait, in milliseconds.
 */
export const RECONNECT_CAP_MS = 16_000;

/**
 * How long a restored connection must stay up, in milliseconds, before a later loss starts a new streak at the first
 * wait instead of continuing the doubling.
 */
export const RECONNECT_STREAK_MS = 60_000;

/**
 * How long the mark shows that the connection is back before it clears, in milliseconds.
 */
export const RESTORED_MARK_MS = 2_000;

/**
 * What the mark shows while the run waits for an attempt: which attempt of how many is next and how long until it.
 */
export interface ReconnectWait {
  readonly attempt: number;
  readonly total: number;
  readonly delayMs: number;
}

/**
 * What a run reads and does: the page's timer and its draw, the opening of one attempt, the word to the mark, and
 * the end of the run when its attempts are spent. The timer and the draw are the page's own unless a test hands in
 * others.
 */
export interface ReconnectDeps {
  /**
   * Runs a callback after a delay.
   *
   * @param delayMs The delay in milliseconds.
   * @param run The callback.
   * @returns A function that cancels it.
   */
  readonly schedule: (delayMs: number, run: () => void) => () => void;
  /**
   * A draw from 0 up to and including 1, which spreads the waits of pages that closed together.
   *
   * @returns The draw.
   */
  readonly random: () => number;
  /**
   * The clock the streak is measured on.
   *
   * @returns Milliseconds since any fixed moment.
   */
  readonly now: () => number;
  /**
   * Opens one attempt. The attempt's end is the run's `failed` or `restored`.
   *
   * @param attempt The attempt's number, from 1.
   */
  readonly open: (attempt: number) => void;
  /**
   * Tells the mark which attempt waits and for how long.
   *
   * @param wait The attempt and its delay.
   */
  readonly waiting: (wait: ReconnectWait) => void;
  /**
   * Called once when every attempt has failed.
   */
  readonly exhausted: () => void;
}

/**
 * The wait before an attempt: the ceiling doubles from `RECONNECT_FIRST_MS` to `RECONNECT_CAP_MS`, and the wait lies
 * from half the ceiling to the ceiling by the draw.
 *
 * @param attempt The attempt's place in its streak, from 1: its number in the run plus the attempts the streak's
 *   earlier runs made.
 * @param random A draw from 0 up to and including 1.
 * @returns The wait in milliseconds.
 * @example
 * reconnectDelayMs(2, () => 0.5);
 * // 3_000
 */
export function reconnectDelayMs(attempt: number, random: () => number): number {
  const ceiling = Math.min(RECONNECT_CAP_MS, RECONNECT_FIRST_MS * 2 ** (attempt - 1));
  return ceiling / 2 + random() * (ceiling / 2);
}

/**
 * The page's own timer.
 *
 * @param delayMs The delay in milliseconds.
 * @param run The callback.
 * @returns A function that cancels it.
 */
export function afterDelay(delayMs: number, run: () => void): () => void {
  const timer = setTimeout(run, delayMs);
  return () => clearTimeout(timer);
}

/**
 * The deps of the page: its timer, `Math.random`, which only spreads the waits of pages and decides nothing shared, and `Date.now`.
 *
 * @param rest What the page supplies itself: the opening of an attempt, the word to the mark and the end of the run.
 * @param timing A timer and a draw in place of the page's own, which a test hands in.
 * @returns The deps.
 */
export function pageReconnectDeps(rest: Pick<ReconnectDeps, "open" | "waiting" | "exhausted">, timing?: Partial<Pick<ReconnectDeps, "schedule" | "random" | "now">>): ReconnectDeps {
  return { schedule: afterDelay, random: Math.random, now: Date.now, ...timing, ...rest };
}

/**
 * One background reconnect: the attempts, one after another, each after its wait. `start` begins the first wait;
 * each attempt the page opens ends in `failed`, which waits for the next or, past the last, ends the run, or in
 * `restored`; `cancel` ends it from outside.
 */
export class Reconnection {
  private attempt = 0;
  private spent = 0;
  private upSince: number | null = null;
  private active = false;
  private cancelWait: (() => void) | null = null;

  /**
   * @param deps What the run reads and does.
   * @param total How many attempts it makes.
   */
  public constructor(
    private readonly deps: ReconnectDeps,
    private readonly total: number = RECONNECT_ATTEMPTS,
  ) {}

  /**
   * Whether a run is under way: from `start` until the connection is restored, the attempts are spent or it is
   * cancelled.
   *
   * @returns True while it is.
   */
  public get running(): boolean {
    return this.active;
  }

  /**
   * Begins a run at its first attempt, ending any before it. The run's waits continue the doubling of the streak the
   * last restored connection belonged to, unless that connection stayed up `RECONNECT_STREAK_MS`.
   */
  public start(): void {
    this.cancel();
    if (this.upSince !== null && this.deps.now() - this.upSince >= RECONNECT_STREAK_MS) {
      this.spent = 0;
    }
    this.upSince = null;
    this.active = true;
    this.attempt = 0;
    this.next();
  }

  /**
   * The attempt just opened has failed: its connection closed or the server stayed silent. Waits for the next, or ends
   * the run when that was the last. Nothing happens with no run.
   */
  public failed(): void {
    if (!this.active) {
      return;
    }
    if (this.attempt >= this.total) {
      this.active = false;
      this.deps.exhausted();
      return;
    }
    this.next();
  }

  /**
   * The connection is back: the run ends, nothing is tried after, and the streak notes when the connection came back.
   */
  public restored(): void {
    if (this.active) {
      this.spent += this.attempt;
      this.upSince = this.deps.now();
    }
    this.cancel();
  }

  /**
   * Ends the run, and any wait in it.
   */
  public cancel(): void {
    this.active = false;
    this.cancelWait?.();
    this.cancelWait = null;
  }

  /**
   * Waits for the next attempt and opens it when the wait is over.
   */
  private next(): void {
    this.attempt += 1;
    const attempt = this.attempt;
    const delayMs = reconnectDelayMs(this.spent + attempt, this.deps.random);
    this.deps.waiting({ attempt, total: this.total, delayMs });
    this.cancelWait = this.deps.schedule(delayMs, () => {
      this.cancelWait = null;
      if (this.active && this.attempt === attempt) {
        this.deps.open(attempt);
      }
    });
  }
}
